const sameItems = (values, otherValues) => values.length === otherValues.length
  && values.every(value => otherValues.includes(value));

const splitValues = rawValue => [].concat(rawValue)
  .flatMap(value => (typeof value === 'string' ? value.split(',') : []))
  .filter(Boolean);

export const parseList = (rawValue, { allowed, fallback = [] } = {}) => {
  const values = [...new Set(splitValues(rawValue))];
  const selected = allowed ? values.filter(value => allowed.includes(value)) : values;

  return selected.length ? selected : [...fallback];
};

export const serializeList = (values, { fallback = [] } = {}) =>
  values.length === 0 || sameItems(values, fallback) ? undefined : values.join(',');

export const listParam = (options = {}) => ({
  parse: rawValue => parseList(rawValue, options),
  serialize: values => serializeList(values, options),
});

export const textParam = (fallback = '') => ({
  parse: rawValue => rawValue || fallback,
  serialize: value => value === fallback ? undefined : value,
});

export const booleanParam = (fallback = false) => ({
  parse: rawValue => rawValue === undefined ? fallback : `${rawValue}` === 'true',
  serialize: value => value === fallback ? undefined : value.toString(),
});

export const numberParam = (fallback = 0) => ({
  parse: rawValue => parseInt(rawValue) || fallback,
  serialize: value => value === fallback ? undefined : `${value}`,
});

export const parseQueryParams = (query, params) => Object.fromEntries(
  Object.entries(params).map(([key, param]) => [key, param.parse(query[key])]),
);

const buildQuery = (currentQuery, params, values) => {
  const query = { ...currentQuery };

  for (const [key, param] of Object.entries(params)) {
    const serialized = param.serialize(values[key]);

    if (serialized === undefined) {
      delete query[key];
    } else {
      query[key] = serialized;
    }
  }

  return query;
};

const sameQuery = (query, otherQuery) => {
  const keys = Object.keys(query);

  return keys.length === Object.keys(otherQuery).length
    && keys.every(key => query[key] === otherQuery[key]);
};

// The current route only changes once a navigation has committed, so calls made
// before then must build on the last query written, not on the stale route.
const pendingQueries = new WeakMap();

export const syncQueryParams = (router, params, values) => {
  const current = pendingQueries.get(router) ?? router.currentRoute.value.query;
  const query = buildQuery(current, params, values);

  if (sameQuery(query, current)) {
    return;
  }

  pendingQueries.set(router, query);
  router.replace({ query }).finally(() => {
    if (pendingQueries.get(router) === query) {
      pendingQueries.delete(router);
    }
  });
};
